import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { PageHeader } from '@/components/common/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { clearPurchaseIntent, storePurchaseIntent } from '@/lib/auth/intent-storage';
import { ENTITY_TYPES } from '@/lib/constants/entity-types';
import { PURCHASE_INTENT } from '@/lib/constants/issue-report';
import { ROLES, hasAnyRole } from '@/lib/constants/roles';
import { ROUTES, billingReviewPath } from '@/lib/constants/routes';
import { withPurchaseIntent } from '@/lib/utils/purchase-intent.utils';
import { ConfirmStartupStep } from '@/features/startups/components/ConfirmStartupStep';
import { IssueReportJourneyState } from './components/IssueReportJourneyState';
import { DATA_ROOM_STRINGS } from './constants';
import { useDataRoomEntitlement } from './hooks/useDataRoomEntitlement';
import { resolveIssueReportStep, toJourneyQuery } from './issue-report-journey';

/**
 * Steps that end the journey, so a stored intent must not outlive them.
 * Checkout is not one: the intent lasts until the confirmed return, which
 * still shows the journey's progress bar (FR-224).
 */
const TERMINAL_STEPS: ReadonlySet<string> = new Set(['data_room', 'admin_only', 'coming_soon']);

/**
 * `/issue-report/start` (FR-204, FR-205, design §38): where the public Due
 * Diligence page's Issue Report CTA, and every login or Create Startup that
 * carried the purchase intent, ends up. It decides the next step with
 * `resolveIssueReportStep` and either redirects (login, Data Room, the review
 * step with the offer's plan preselected) or stops on a state that says why no
 * checkout opens. Public layout: it must work logged out.
 *
 * The intent is stored while the journey is still going (login, Create
 * Startup) so the hops in between resume here, and forgotten once the journey
 * ends: a hand-off to the Data Room or checkout, or a terminal screen (Coming
 * Soon, "only a Startup Admin"). Only the transient error screen keeps it, so
 * Retry still works; its way back forgets it (FR-206).
 */
export function IssueReportStartPage() {
  const offer = useIssueReportOffer();
  const auth = useAuth();
  const { user } = auth;
  // Only a Startup Admin's access matters, so nobody else triggers the read.
  const startupId =
    user?.activeMembership?.entityType === ENTITY_TYPES.STARTUP && hasAnyRole(user.roles, [ROLES.STARTUP_ADMIN])
      ? user.activeMembership.entityId
      : null;
  const access = useDataRoomEntitlement(startupId);

  const step = resolveIssueReportStep({
    offer: toJourneyQuery(offer),
    auth,
    access: toJourneyQuery(access),
  });

  useEffect(() => {
    if (step.kind === 'login' || step.kind === 'create_startup') {
      storePurchaseIntent(PURCHASE_INTENT.ISSUE_REPORT);
    } else if (TERMINAL_STEPS.has(step.kind)) {
      clearPurchaseIntent();
    }
  }, [step.kind]);

  switch (step.kind) {
    case 'login':
      return <Navigate to={withPurchaseIntent(ROUTES.AUTH.LOGIN, PURCHASE_INTENT.ISSUE_REPORT)} replace />;
    case 'data_room':
      return <Navigate to={ROUTES.DATA_ROOM} replace />;
    case 'checkout':
      return <Navigate to={billingReviewPath(step.planId)} replace />;
    case 'create_startup':
      // One "Confirm your startup" step instead of the full Create Startup
      // form (FR-225); no way back to the Due Diligence page (FR-226).
      return (
        <div className="bg-page-bg min-h-full">
          <PageHeader title={DATA_ROOM_STRINGS.JOURNEY.TITLE} />
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 py-10">
            <IssueReportJourneySteps stage="startup" />
            <ConfirmStartupStep />
          </div>
        </div>
      );
    default:
      return (
        <div className="bg-page-bg min-h-full">
          <PageHeader title={DATA_ROOM_STRINGS.JOURNEY.TITLE} />
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-10">
            <IssueReportJourneyState
              kind={step.kind}
              onRetry={() => {
                // Only what failed, and never the access read without a startup:
                // `refetch` ignores `enabled` and would request `/startups/null/...`.
                if (offer.isError) void offer.refetch();
                if (startupId && access.isError) void access.refetch();
              }}
            />
          </div>
        </div>
      );
  }
}
